import _ from 'lodash';
import {DateTime} from 'luxon';
import {createSelector} from 'reselect';

import {hasCommonNamespace, isTeammateActive, isVisitorTeammate} from '../../../helpers/modelHelpers';
import {isGlobalAdmin} from '../../../helpers/models/accessHelpers';
import {isTeamNamespace} from '../../../helpers/models/namespaceHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {
  TeammateAccountStatusesEnum,
  TeammateLicenseTypesEnum,
  TeammateModel,
} from '../../../models/api/teammateModel';
import {TeamModel} from '../../../models/api/teamModel';
import {AppState} from '../../appState';
import {getAccess} from '../dataSelectors';
import {getIsBillingContact} from '../preferences/preferencesSelectors';

/*
 * Base selectors.
 */

export function getUserId(state: AppState) {
  return state.data.userId;
}

/** Get the map of all teammates. */
export function getTeammates(state: AppState) {
  return state.data.teammates;
}

/*
 * User global selectors.
 */

export const getUser = createSelector([getUserId, getTeammates], (userId, teammates) => {
  const user = teammates[userId];
  if (!user) {
    throw new Error('User not found.');
  }

  return user;
});

export const getUserGivenName = createSelector(getUser, (user) => user.givenName);

export const getUserEmail = createSelector(getUser, (user) => user.email);

export const getUserPhone = createSelector(getUser, (user) => user.phone);

export const getUserNamespace = createSelector(getUser, (user) => user.namespace);

export const getUserLicenseType = createSelector(getUser, (user) => user.licenseType);

export const getIsUserVisitor = createSelector(getUser, (user) => isVisitorTeammate(user));

export const getUserAlias = createSelector(getUser, (user) => user.alias);

export const getUserNotificationRules = createSelector(getUser, (user) => user.notificationRules);

/** Current new user guideline is four (4) days. */
const newUserCutoffTime = DateTime.local().minus({days: 4}).toMillis();
export const getIsUserNewUser = createSelector(getUser, (user) => user.createdAt > newUserCutoffTime);

/*
 * Teammates global selectors.
 */

/** Get a a flat list of all teammates, sorted by name. */
export const getFlatTeammates = createSelector(getTeammates, (teammates) =>
  _(teammates)
    .values()
    .compact()
    .sortBy((t) => (t.givenName + t.familyName).toLowerCase())
    .value(),
);

export const getOtherTeammatesAliases = createSelector(
  [getFlatTeammates, getUserAlias],
  (teammates, userAlias) => new Set(teammates.filter((t) => t.alias !== userAlias).map((t) => t.alias)),
);

/** Get a flat list of all active teammates, sorted by name. */
export const getFlatActiveTeammates = createSelector(getFlatTeammates, (teammates) =>
  teammates.filter((t) => isTeammateActive(t)),
);

/** Get a flat list of all active or pending teammates, sorted by name. */
export const getFlatActiveOrPendingTeammates = createSelector(getFlatTeammates, (teammates) =>
  teammates.filter(
    (t) =>
      (t.accountStatus === TeammateAccountStatusesEnum.ACTIVE &&
        t.licenseType !== TeammateLicenseTypesEnum.VISITOR) ||
      t.accountStatus === TeammateAccountStatusesEnum.PENDING ||
      t.accountStatus === TeammateAccountStatusesEnum.PENDING_EMAIL,
  ),
);

/** Get a flat list of all active, pending, or blocked teammates, sorted by name. */
export const getFlatActivePendingOrBlockedTeammates = createSelector(getFlatTeammates, (teammates) =>
  teammates.filter(
    (t) =>
      (t.accountStatus === TeammateAccountStatusesEnum.ACTIVE &&
        t.licenseType !== TeammateLicenseTypesEnum.VISITOR) ||
      t.accountStatus === TeammateAccountStatusesEnum.PENDING ||
      t.accountStatus === TeammateAccountStatusesEnum.PENDING_EMAIL ||
      t.accountStatus === TeammateAccountStatusesEnum.BLOCKED,
  ),
);

/** Get a flat list of all teammates except our user. */
export const getFlatOtherActiveTeammates = createSelector(
  [getUser, getFlatActiveTeammates],
  (user, teammates) => teammates.filter((t) => t !== user),
);

/** Get a flat list of all teammates with a team in common with our user. */
export const getTeammatesInCommonTeam = createSelector(
  [getUser, getFlatOtherActiveTeammates],
  (user, teammates) => teammates.filter((t) => hasCommonNamespace(user, t)),
);

export const getHasTeammates = createSelector(getTeammatesInCommonTeam, (teammates) => teammates.length > 0);

export const getTeammatesInCommonTeamIds = createSelector(
  getTeammatesInCommonTeam,
  (teammatesInCommonTeam) => new Set(teammatesInCommonTeam.map((t) => t.id)),
);

export const getFlatAdmins = createSelector(getFlatTeammates, (flatTeammates) =>
  flatTeammates.filter(
    (t) => t.access.global && t.access.global.roles && t.access.global.roles.includes('admin'),
  ),
);

export const getIsUserGlobalAdmin = createSelector([getAccess], (access) => isGlobalAdmin(access));

export const getFlatGlobalAdmins = createSelector(getFlatActiveTeammates, (teammates) =>
  _(teammates)
    .filter((teammate) =>
      Boolean(teammate.access && teammate.access.global && teammate.access.global.roles.includes('admin')),
    )
    .value(),
);

export const getFirstGlobalAdmin = createSelector(getFlatGlobalAdmins, (globalAdmins) =>
  _(globalAdmins)
    .sortBy((t) => t.createdAt)
    .first(),
);

const twentyDaysAgoMillis = DateTime.local().minus({days: 20}).toMillis();
export const getHomePendingTeammates = createSelector(
  [getFlatActiveOrPendingTeammates, getUserId],
  (teammates, userId) =>
    _(teammates)
      .filter(
        (t) =>
          t.licenseType !== TeammateLicenseTypesEnum.TEMPLATE &&
          t.createdAt >= twentyDaysAgoMillis &&
          t.id !== userId &&
          !t.email.startsWith('charlie_saturn+'),
      )
      .sortBy((t) => t.createdAt)
      .take(30)
      .value(),
);

export const getIsUserFirstGlobalAdmin = createSelector(
  [getUserId, getFirstGlobalAdmin],
  (userId, firstGlobalAdmin) => Boolean(firstGlobalAdmin && userId === firstGlobalAdmin.id),
);

export const getIsUserBillingContact = createSelector(
  [getUser, getIsBillingContact],
  (user, isBillingContact) => isBillingContact,
);

export const getTeammatesByTeam = createSelector([getFlatTeammates], (teammates) =>
  teammates.reduce((acc: {[key: string]: TeammateModel[]}, teammate) => {
    Object.keys(teammate.access).forEach((namespace) => {
      if (isTeamNamespace(namespace)) {
        if (!acc[namespace]) {
          acc[namespace] = [];
        }

        acc[namespace].push(teammate);
      }
    });
    return acc;
  }, {}),
);

export const makeGetActiveTeammatesForTeam = (getTeam: AppParametricSelector<any, TeamModel>) =>
  createSelector([getTeam, getTeammatesByTeam], (team, teammatesByTeam) => {
    const teammates = teammatesByTeam[team.namespace];
    if (teammates) {
      return teammates.filter((teammate) => teammate.accountStatus === TeammateAccountStatusesEnum.ACTIVE);
    }

    return [];
  });

export const makeGetActiveTeammateForEmail = (getEmail: AppParametricSelector<any, string | undefined>) =>
  createSelector([getEmail, getFlatActiveTeammates], (email, teammates) =>
    teammates.find((t) => t.email === email),
  );
